<template>
  <div class="supplier-mapping-tab">
    <Card title="Leverancier mapping" :subtitle="supplierName">
      <template #actions>
        <Btn variant="secondary" size="sm" @click="handlePreview" :disabled="!hasMapping" v-if="!localOnly">
          Preview bestelling
        </Btn>
        <Btn variant="primary" size="sm" @click="handleSave" :loading="saving">Opslaan</Btn>
      </template>

      <div class="supplier-mapping-tab__header">
        <div class="row">
          <div class="col-lg-4">
            <TextInput
              id="supplier-product-code"
              v-model="form.supplierProductCode"
              label="Leverancier productcode"
              placeholder="bijv. 10000"
            />
          </div>
          <div class="col-lg-4">
            <SelectInput
              id="catalog-select"
              v-model="form.catalogId"
              :options="catalogOptions"
              label="Catalogus"
              placeholder="Geen catalogus geselecteerd"
            />
          </div>
          <div class="col-lg-4" style="display: flex; align-items: flex-end">
            <Btn variant="secondary" size="sm" @click="handleImportCatalog">
              Catalogus importeren
            </Btn>
          </div>
        </div>
      </div>
    </Card>

    <!-- Option Mappings -->
    <Card title="Configurator-opties → Leverancierscodes" class="mt-3">
      <div class="mapping-table">
        <div class="mapping-table__header">
          <div class="mapping-table__col mapping-table__col--option">Configurator optie</div>
          <div class="mapping-table__col mapping-table__col--code">Code</div>
          <div class="mapping-table__col mapping-table__col--type">Type</div>
          <div class="mapping-table__col mapping-table__col--actions">Actie</div>
        </div>

        <OptionMappingRow
          v-for="(mapping, index) in form.optionMappings"
          :key="mapping.id"
          :mapping="mapping"
          :product-options="productOptions"
          :catalog-fields="activeCatalogFields"
          @update="updateOptionMapping(index, $event)"
          @remove="removeOptionMapping(index)"
          @configure="openMappingConfig(index)"
        />

        <div v-if="unmappedOptions.length > 0" class="mapping-table__unmapped">
          <div v-for="opt in unmappedOptions" :key="opt.id" class="mapping-table__unmapped-row">
            <span class="mapping-table__unmapped-label">{{ opt.label }}</span>
            <StatusBadge type="supplier_mapping" status="not_mapped" size="sm" />
            <Btn variant="plain" size="xs" @click="addMappingForOption(opt)">+ Map</Btn>
          </div>
        </div>

        <Btn variant="dashed" size="sm" class="mt-2" @click="addOptionMapping">
          + Optie toevoegen
        </Btn>
      </div>
    </Card>

    <!-- Static Values -->
    <Card title="Standaard bestelwaarden" class="mt-3">
      <p class="text-muted text-sm mb-3">
        Vaste waarden die altijd meegaan in de bestelling, ongeacht de configuratie.
      </p>

      <StaticValueRow
        v-for="(sv, index) in form.staticValues"
        :key="index"
        :value="sv"
        :catalog-fields="activeCatalogFields"
        @update="updateStaticValue(index, $event)"
        @remove="removeStaticValue(index)"
      />

      <Btn variant="dashed" size="sm" class="mt-2" @click="addStaticValue">+ Waarde toevoegen</Btn>
    </Card>

    <!-- Conditional Rules -->
    <Card title="Conditionele regels" class="mt-3">
      <p class="text-muted text-sm mb-3">
        Regels die waarden overschrijven op basis van configuratie-keuzes.
      </p>

      <ConditionalRuleRow
        v-for="(rule, index) in form.conditionalRules"
        :key="rule.id"
        :rule="rule"
        :product-options="productOptions"
        @update="updateConditionalRule(index, $event)"
        @remove="removeConditionalRule(index)"
      />

      <Btn variant="dashed" size="sm" class="mt-2" @click="addConditionalRule">
        + Regel toevoegen
      </Btn>
    </Card>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { productMappingService } from '@/services/productMappingService';
  import { supplierCatalogService } from '@/services/supplierCatalogService';
  import Card from '@/components/partials/Card.vue';
  import Btn from '@/components/partials/Btn.vue';
  import StatusBadge from '@/components/partials/StatusBadge.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import OptionMappingRow from './mapping/OptionMappingRow.vue';
  import StaticValueRow from './mapping/StaticValueRow.vue';
  import ConditionalRuleRow from './mapping/ConditionalRuleRow.vue';
  import ChoiceMappingModal from './mapping/ChoiceMappingModal.vue';
  import TransformConfigModal from './mapping/TransformConfigModal.vue';
  import MappingPreviewModal from './mapping/MappingPreviewModal.vue';
  import CatalogImportModal from './mapping/CatalogImportModal.vue';

  export default defineComponent({
    name: 'SupplierMappingTab',

    components: {
      Card,
      Btn,
      StatusBadge,
      TextInput,
      SelectInput,
      OptionMappingRow,
      StaticValueRow,
      ConditionalRuleRow,
    },

    emits: ['save'],
    props: {
      productId: { type: String, required: true },
      supplierId: { type: String, required: true },
      supplierName: { type: String, default: '' },
      productOptions: { type: Array, default: () => [] },
      presetMapping: { type: Object, default: null },
      localOnly: { type: Boolean, default: false },
    },

    setup(props, { emit }) {
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const loading = ref(false);
      const saving = ref(false);
      const catalogs = ref([]);
      const activeCatalog = ref(null);

      const form = ref({
        supplierProductCode: '',
        catalogId: null,
        optionMappings: [],
        staticValues: [],
        conditionalRules: [],
      });

      const hasMapping = computed(() => {
        return form.value.optionMappings.length > 0 || form.value.staticValues.length > 0;
      });

      const catalogOptions = computed(() => {
        return [
          { value: null, label: 'Geen catalogus' },
          ...catalogs.value.map((c) => ({
            value: c.id,
            label: `${c.name}${c.product_category ? ` (${c.product_category})` : ''}`,
          })),
        ];
      });

      const activeCatalogFields = computed(() => {
        if (!form.value.catalogId) return [];
        const cat = catalogs.value.find((c) => c.id === form.value.catalogId);
        return cat?.fields || [];
      });

      const unmappedOptions = computed(() => {
        const mappedIds = new Set(
          form.value.optionMappings.filter((m) => m.optionId).map((m) => m.optionId)
        );
        return props.productOptions
          .filter((opt) => !mappedIds.has(opt.id))
          .map((opt) => ({ id: opt.id, label: opt.label || opt.name || opt.id }));
      });

      function generateId() {
        return 'map_' + Math.random().toString(36).substring(2, 11);
      }

      // --- Option Mappings ---

      function addOptionMapping() {
        form.value.optionMappings.push({
          id: generateId(),
          optionId: null,
          optionLabel: '',
          supplierCode: '',
          valueType: 'direct',
          transforms: [],
          choiceMappings: {},
          defaultValue: null,
          compositeRule: null,
          formula: null,
        });
      }

      function addMappingForOption(opt) {
        const optionDef = props.productOptions.find((o) => o.id === opt.id);
        const isChoice = optionDef?.type === 'single-choice' || optionDef?.type === 'multi-choice';
        const hasSupplierCodes =
          isChoice && optionDef?.choiceConfig?.choices?.some((c) => c.supplierCode);

        form.value.optionMappings.push({
          id: generateId(),
          optionId: opt.id,
          optionLabel: opt.label,
          supplierCode: '',
          valueType: hasSupplierCodes ? 'choice_auto' : isChoice ? 'choice_map' : 'direct',
          transforms: [],
          choiceMappings: {},
          defaultValue: null,
          compositeRule: null,
          formula: null,
        });
      }

      function updateOptionMapping(index, updated) {
        form.value.optionMappings[index] = { ...form.value.optionMappings[index], ...updated };
      }

      function removeOptionMapping(index) {
        form.value.optionMappings.splice(index, 1);
      }

      function openMappingConfig(index) {
        const mapping = form.value.optionMappings[index];
        const optionDef = props.productOptions.find((o) => o.id === mapping.optionId);

        if (mapping.valueType === 'choice_map') {
          uiStore.addModal(ChoiceMappingModal, {
            hasComponent: true,
            title: `Keuzes mappen: ${mapping.optionLabel}`,
            size: 'lg',
            mapping,
            optionChoices: optionDef?.choiceConfig?.choices || [],
            catalogFields: activeCatalogFields.value,
            supplierCode: mapping.supplierCode,
            onSubmit: (result) => {
              updateOptionMapping(index, {
                choiceMappings: result.choiceMappings,
                defaultValue: result.defaultValue,
              });
            },
          });
        } else if (mapping.valueType === 'transform') {
          uiStore.addModal(TransformConfigModal, {
            hasComponent: true,
            title: `Transform: ${mapping.optionLabel}`,
            size: 'md',
            transforms: mapping.transforms || [],
            onSubmit: (result) => {
              updateOptionMapping(index, { transforms: result });
            },
          });
        }
      }

      // --- Static Values ---

      function addStaticValue() {
        form.value.staticValues.push({
          supplierCode: '',
          value: '',
          label: '',
        });
      }

      function updateStaticValue(index, updated) {
        form.value.staticValues[index] = { ...form.value.staticValues[index], ...updated };
      }

      function removeStaticValue(index) {
        form.value.staticValues.splice(index, 1);
      }

      // --- Conditional Rules ---

      function addConditionalRule() {
        form.value.conditionalRules.push({
          id: generateId(),
          name: '',
          priority: 10,
          condition: {
            type: 'option_value_gt',
            optionId: null,
            threshold: 0,
          },
          action: {
            type: 'set_value',
            supplierCode: '',
            value: '',
            label: '',
          },
        });
      }

      function updateConditionalRule(index, updated) {
        form.value.conditionalRules[index] = { ...form.value.conditionalRules[index], ...updated };
      }

      function removeConditionalRule(index) {
        form.value.conditionalRules.splice(index, 1);
      }

      // --- Load / Save ---

      function applyPreset(mapping) {
        form.value.supplierProductCode = mapping.supplier_product_code || '';
        form.value.catalogId = mapping.catalog_id || null;
        form.value.optionMappings = mapping.option_mappings || [];
        form.value.staticValues = mapping.static_values || [];
        form.value.conditionalRules = mapping.conditional_rules || [];
      }

      async function loadMapping() {
        loading.value = true;
        try {
          const mapping = await productMappingService.getMapping(props.productId, props.supplierId);
          if (mapping) {
            form.value.supplierProductCode = mapping.supplier_product_code || '';
            form.value.catalogId = mapping.catalog_id || null;
            form.value.optionMappings = mapping.option_mappings || [];
            form.value.staticValues = mapping.static_values || [];
            form.value.conditionalRules = mapping.conditional_rules || [];
          }
        } catch (e) {
          // 404 is fine, means no mapping yet
        } finally {
          loading.value = false;
        }
      }

      async function loadCatalogs() {
        try {
          catalogs.value = await supplierCatalogService.getCatalogs(props.supplierId);
        } catch (e) {
          catalogs.value = [];
        }
      }

      async function handleSave() {
        if (props.localOnly) {
          emit('save', {
            supplier_product_code: form.value.supplierProductCode,
            option_mappings: form.value.optionMappings,
            static_values: form.value.staticValues,
            conditional_rules: form.value.conditionalRules,
            catalog_id: form.value.catalogId,
          });
          toastStore.addToast({
            type: 'success',
            message: $t('products.supplier_mapping.save_success'),
          });
          return;
        }
        saving.value = true;
        try {
          await productMappingService.saveMapping(props.productId, {
            supplier_id: props.supplierId,
            supplier_product_code: form.value.supplierProductCode,
            option_mappings: form.value.optionMappings,
            static_values: form.value.staticValues,
            conditional_rules: form.value.conditionalRules,
            catalog_id: form.value.catalogId,
          });
          toastStore.addToast({
            type: 'success',
            message: $t('products.supplier_mapping.save_success'),
          });
        } catch (e) {
          toastStore.addToast({
            type: 'error',
            message:
              e.response?.data?.message || $t('products.supplier_mapping.save_failed'),
          });
        } finally {
          saving.value = false;
        }
      }

      function handlePreview() {
        uiStore.addModal(MappingPreviewModal, {
          hasComponent: true,
          title: 'Preview bestelling',
          size: 'lg',
          productId: props.productId,
          supplierId: props.supplierId,
        });
      }

      function handleImportCatalog() {
        uiStore.addModal(CatalogImportModal, {
          hasComponent: true,
          title: 'Catalogus importeren',
          size: 'md',
          supplierId: props.supplierId,
          onSubmit: () => {
            loadCatalogs();
          },
        });
      }

      onMounted(() => {
        if (props.localOnly) {
          if (props.presetMapping) applyPreset(props.presetMapping);
          return;
        }
        loadMapping();
        loadCatalogs();
      });

      watch(
        () => props.presetMapping,
        (mapping) => {
          if (props.localOnly && mapping) applyPreset(mapping);
        }
      );

      watch(
        () => props.supplierId,
        () => {
          loadMapping();
          loadCatalogs();
        }
      );

      return {
        form,
        loading,
        saving,
        hasMapping,
        catalogOptions,
        activeCatalogFields,
        unmappedOptions,
        addOptionMapping,
        addMappingForOption,
        updateOptionMapping,
        removeOptionMapping,
        openMappingConfig,
        addStaticValue,
        updateStaticValue,
        removeStaticValue,
        addConditionalRule,
        updateConditionalRule,
        removeConditionalRule,
        handleSave,
        handlePreview,
        handleImportCatalog,
      };
    },
  });
</script>

<style scoped lang="scss">
  .supplier-mapping-tab {
    &__header {
      margin-bottom: 1rem;
    }
  }

  .mapping-table {
    &__header {
      display: flex;
      gap: 0.5rem;
      padding: 0.5rem 0;
      font-weight: 600;
      font-size: 0.8125rem;
      color: var(--text-secondary);
      border-bottom: 1px solid var(--border-color);
      margin-bottom: 0.5rem;
    }

    &__col {
      &--option {
        flex: 2;
      }
      &--code {
        flex: 1;
        max-width: 100px;
      }
      &--type {
        flex: 1;
        max-width: 140px;
      }
      &--actions {
        flex: 1;
        max-width: 120px;
        text-align: right;
      }
    }

    &__unmapped {
      border-top: 1px dashed var(--border-color);
      margin-top: 0.75rem;
      padding-top: 0.75rem;
    }

    &__unmapped-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.375rem 0;
    }

    &__unmapped-label {
      flex: 1;
      font-size: 0.875rem;
      color: var(--text-secondary);
    }
  }

  .mt-2 {
    margin-top: 0.5rem;
  }
  .mt-3 {
    margin-top: 1rem;
  }
  .mb-3 {
    margin-bottom: 1rem;
  }
  .text-muted {
    color: var(--text-secondary);
  }
  .text-sm {
    font-size: 0.8125rem;
  }
</style>
